// ---------------------------------------------
// Helper Functions - DeSight Shopify Theme
// ---------------------------------------------
// Wiederverwendbare Hilfsfunktionen
// ---------------------------------------------

/**
 * Debounce Funktion
 * Verzögert die Ausführung einer Funktion
 * @param {Function} func - Die auszuführende Funktion
 * @param {Number} wait - Wartezeit in Millisekunden
 * @returns {Function}
 */
export function debounce(func, wait = 300) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Throttle Funktion
 * Begrenzt die Ausführungshäufigkeit einer Funktion
 * @param {Function} func - Die auszuführende Funktion
 * @param {Number} limit - Zeitlimit in Millisekunden
 * @returns {Function}
 */
export function throttle(func, limit = 300) {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

/**
 * Query Selector Helper
 * Vereinfachte DOM-Selektion
 * @param {String} selector - CSS Selector
 * @param {Element} context - Kontext-Element (optional)
 * @returns {Element|null}
 */
export function $(selector, context = document) {
  return context.querySelector(selector);
}

/**
 * Query Selector All Helper
 * Vereinfachte DOM-Selektion für mehrere Elemente
 * @param {String} selector - CSS Selector
 * @param {Element} context - Kontext-Element (optional)
 * @returns {Array}
 */
export function $$(selector, context = document) {
  return Array.from(context.querySelectorAll(selector));
}

/**
 * Element erstellen
 * @param {String} tag - HTML Tag
 * @param {Object} attributes - Attribute als Objekt
 * @param {String} content - Textinhalt (optional)
 * @returns {Element}
 */
export function createElement(tag, attributes = {}, content = '') {
  const element = document.createElement(tag);
  
  Object.keys(attributes).forEach(key => {
    if (key === 'class') {
      element.className = attributes[key];
    } else if (key === 'dataset') {
      Object.keys(attributes[key]).forEach(dataKey => {
        element.dataset[dataKey] = attributes[key][dataKey];
      });
    } else {
      element.setAttribute(key, attributes[key]);
    }
  });
  
  if (content) {
    element.textContent = content;
  }
  
  return element;
}

/**
 * Prüft ob Element im Viewport sichtbar ist
 * @param {Element} element - Zu prüfendes Element
 * @returns {Boolean}
 */
export function isInViewport(element) {
  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
}

/**
 * Formatiert Preis
 * @param {Number} cents - Preis in Cents
 * @param {String} currency - Währung (optional)
 * @returns {String}
 */
export function formatMoney(cents, currency = '€') {
  const amount = (cents / 100).toFixed(2);
  return `${amount} ${currency}`;
}

/**
 * Fetch Helper mit Error Handling
 * @param {String} url - URL
 * @param {Object} options - Fetch Options
 * @returns {Promise}
 */
export async function fetchJSON(url, options = {}) {
  try {
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Fetch error:', error);
    throw error;
  }
}

/**
 * Parse text with **bold** syntax and apply button color
 * @param {String} text - Text to parse
 * @returns {String} HTML string with highlighted parts
 */
export function parseBoldText(text) {
  if (!text) return '';
  const buttonColor = 'rgb(122, 185, 41)';
  const regex = /\*\*(.+?)\*\*/g;
  return text.replace(regex, `<span style="color: ${buttonColor}">$1</span>`);
}

